// Organisations (design/isomorphic orgs): a card per tenant with its Incus project, status,
// what it holds and how much of its quota its environments take.
import { Buildings, Cube, FlowArrow, ListChecks } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type { Environment, Organisation, Run, Workflow } from "../api/client";
import { environmentsQuery, orgsQuery, runsQuery, workflowsQuery } from "../api/queries";
import { usageQuery } from "../dashboard/api";
import { useT } from "../i18n";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { Meter } from "../ui/Meter";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, StatusPill } from "../ui/status";
import { organisationTone } from "../ui/tones";
import { gibCount, usd } from "../workloads/format";
import { day } from "./format";
import { sectionCrumbs } from "../layout/nav";

function Count({ icon, n, label }: { icon: ReactNode; n: number; label: (n: number) => string }) {
  return (
    <div className="text-center">
      <span className="mx-auto flex justify-center text-gray-500 [&>svg]:h-5 [&>svg]:w-5">{icon}</span>
      <div className="tabular mt-1 font-display text-lg font-bold text-gray-900">{n}</div>
      <div className="text-[11.5px] text-gray-500">{label(n)}</div>
    </div>
  );
}

function Spend({ orgId }: { orgId: string }) {
  const t = useT().workloads.orgs;
  const usage = useQuery(usageQuery(orgId));
  if (!usage.data) return null;
  return t.spend(<span className="tabular font-medium text-gray-800">{usd(usage.data.total_usd)}</span>);
}

interface Holdings {
  environments: Environment[];
  workflows: Workflow[];
  runs: Run[];
}

function OrgCard({ org, holds }: { org: Organisation; holds: Holdings }) {
  const { workloads } = useT();
  const t = workloads.orgs;
  const cpu = holds.environments.reduce((n, e) => n + e.limits_cpu, 0);
  const memory = holds.environments.reduce((n, e) => n + e.limits_memory_bytes, 0);
  return (
    <section className={cn(cardClass, "p-5 lg:p-6")} aria-label={org.name}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-lighter font-display text-lg font-extrabold text-primary">
            {org.name.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900">{org.name}</h3>
            <Mono className="text-gray-500">org-{org.slug}</Mono>
          </div>
        </div>
        <StatusPill tone={organisationTone(org.status)} status={org.status} />
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-100/50">
        <Count icon={<Cube weight="duotone" />} n={holds.environments.length} label={t.environments} />
        <Count icon={<FlowArrow weight="duotone" />} n={holds.workflows.length} label={t.workflows} />
        <Count icon={<ListChecks weight="duotone" />} n={holds.runs.length} label={t.recentRuns} />
      </div>
      <div className="mt-5 space-y-4">
        <Meter label={t.cpuQuota} used={cpu} total={org.quota_cpu} unit={workloads.cores} />
        <Meter label={t.memoryQuota} used={gibCount(memory)} total={gibCount(org.quota_memory_bytes)} unit="GiB" />
        <p className="m-0 text-xs text-gray-500">{t.quotaNote(gibCount(org.quota_disk_bytes))}</p>
      </div>
      <div className="mt-5 flex items-center justify-between gap-3 border-t border-muted pt-4 text-[13px]">
        <span className="text-gray-500">
          {t.since(day(org.created_at))}
          <Spend orgId={org.id} />
        </span>
        <Link to="/environments" className="font-medium text-primary">
          {t.environmentsLink}
        </Link>
      </div>
    </section>
  );
}

export default function OrgsPage() {
  const { workloads } = useT();
  const t = workloads.orgs;
  const orgs = useQuery(orgsQuery());
  const environments = useQuery(environmentsQuery());
  const workflows = useQuery(workflowsQuery());
  const runs = useQuery(runsQuery());
  const holds = (o: Organisation): Holdings => ({
    environments: (environments.data ?? []).filter((e) => e.org_id === o.id),
    workflows: (workflows.data ?? []).filter((w) => w.org_id === o.id),
    runs: (runs.data ?? []).filter((r) => r.org_id === o.id),
  });

  return (
    <>
      <PageHeader
        title={t.title}
        breadcrumb={sectionCrumbs("orgs")}
        description={t.description}
      />
      {orgs.isPending ? <Loading /> : null}
      {orgs.isError ? <ErrorState message={orgs.error.message} /> : null}
      {orgs.data?.length === 0 ? (
        <EmptyState icon={<Buildings weight="duotone" />} title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      ) : null}
      <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
        {orgs.data?.map((o) => (
          <OrgCard key={o.id} org={o} holds={holds(o)} />
        ))}
      </div>
    </>
  );
}
